// Vuex module namespaced do workflow-engine (T052).
//
// As actions são a ÚNICA porta entre os componentes e a camada de API — os
// componentes fazem `store.dispatch('workflowEngine/...')`, nunca chamam
// `con.*` nem a api layer direto, nunca fazem `commit` direto.

import { workflowEngineApi } from '@modules/workflow-engine/api/workflow-engine.api'

export const state = () => ({
  triggers: [],
  triggersLoading: false,
  currentTrigger: null,
  formDefinition: null,
  workflows: [],
  workflowsLoading: false,
  catalog: [],
  catalogLoaded: false,
})

export const getters = {
  triggers: (s) => s.triggers,
  triggersLoading: (s) => s.triggersLoading,
  currentTrigger: (s) => s.currentTrigger,
  formDefinition: (s) => s.formDefinition,
  workflows: (s) => s.workflows,
  workflowsLoading: (s) => s.workflowsLoading,
  catalog: (s) => s.catalog,
  catalogLoaded: (s) => s.catalogLoaded,
}

export const mutations = {
  SET_TRIGGERS(s, triggers) {
    s.triggers = Array.isArray(triggers) ? triggers : []
  },
  SET_TRIGGERS_LOADING(s, loading) {
    s.triggersLoading = !!loading
  },
  SET_CURRENT_TRIGGER(s, trigger) {
    s.currentTrigger = trigger || null
  },
  SET_FORM_DEFINITION(s, definition) {
    s.formDefinition = definition || null
  },
  SET_WORKFLOWS(s, workflows) {
    s.workflows = Array.isArray(workflows) ? workflows : []
  },
  SET_WORKFLOWS_LOADING(s, loading) {
    s.workflowsLoading = !!loading
  },
  SET_CATALOG(s, catalog) {
    s.catalog = Array.isArray(catalog) ? catalog : []
    s.catalogLoaded = true
  },
}

export const actions = {
  // Registry (US-1) ----------------------------------------------------------

  async loadTriggers({ commit }, filters = {}) {
    commit('SET_TRIGGERS_LOADING', true)
    try {
      const r = await workflowEngineApi.getTriggers(filters)
      if (r && r.status === 200) {
        commit('SET_TRIGGERS', r.value || [])
      }
      return r
    } finally {
      commit('SET_TRIGGERS_LOADING', false)
    }
  },

  createTrigger(_ctx, { data }) {
    return workflowEngineApi.createTrigger({ data })
  },

  // US-5: persiste config.fields[] via PUT /trigger/<id>.
  async saveTrigger({ commit }, { id, config, ativo }) {
    const data = { config }
    if (ativo !== undefined) data.ativo = ativo
    const r = await workflowEngineApi.saveTrigger({ id, data })
    if (r && r.status === 200) {
      commit('SET_CURRENT_TRIGGER', r.value || null)
    }
    return r
  },

  setTriggerActive(_ctx, { id, ativo }) {
    return workflowEngineApi.setTriggerActive({ id, ativo })
  },

  deleteTrigger(_ctx, { id }) {
    return workflowEngineApi.deleteTrigger({ id })
  },

  // Test-fire (US-7) ---------------------------------------------------------

  testFire(_ctx, { id, samplePayload = null }) {
    return workflowEngineApi.testFire({ id, samplePayload })
  },

  // Workflows (spec-004 canvas) ----------------------------------------------

  async loadWorkflows({ commit }, { ativo = null } = {}) {
    commit('SET_WORKFLOWS_LOADING', true)
    try {
      const r = await workflowEngineApi.getWorkflows({ ativo })
      if (r && r.status === 200) {
        commit('SET_WORKFLOWS', r.value || [])
      }
      return r
    } finally {
      commit('SET_WORKFLOWS_LOADING', false)
    }
  },

  getWorkflow(_ctx, { id }) {
    return workflowEngineApi.getWorkflow({ id })
  },

  createWorkflow(_ctx, { data }) {
    return workflowEngineApi.createWorkflow({ data })
  },

  saveWorkflow(_ctx, { id, data }) {
    return workflowEngineApi.saveWorkflow({ id, data })
  },

  deleteWorkflow(_ctx, { id }) {
    return workflowEngineApi.deleteWorkflow({ id })
  },

  runWorkflow(_ctx, { id }) {
    return workflowEngineApi.runWorkflow({ id })
  },

  getWorkflowExecutions(_ctx, { id, status = null, limit = null }) {
    return workflowEngineApi.getWorkflowExecutions({ id, status, limit })
  },

  getExecution(_ctx, { id }) {
    return workflowEngineApi.getExecution({ id })
  },

  // Catálogo com cache no store: só refaz a chamada quando `force` ou ainda não
  // carregado. Evita refetch a cada abertura do painel de nós.
  async loadCatalog({ commit, state: s }, { force = false } = {}) {
    if (s.catalogLoaded && !force) {
      return { status: 200, message: '', value: s.catalog }
    }
    const r = await workflowEngineApi.getCatalog()
    if (r && r.status === 200) {
      commit('SET_CATALOG', r.value || [])
    }
    return r
  },

  // Form (US-3/US-5) — autenticado; SÓ o token (US-8) -------------------------

  async fetchFormDefinition({ commit }, token) {
    const r = await workflowEngineApi.fetchFormDefinition({ token })
    if (r && r.status === 200) {
      commit('SET_FORM_DEFINITION', r.value || null)
    }
    return r
  },

  submitForm(_ctx, { token, values }) {
    return workflowEngineApi.submitForm({ token, values })
  },
}
